import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Button,
  Grid,
  Alert,
  Loader,
  Center,
  Divider,
  Paper,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconDownload,
  IconFileText,
  IconCalendar,
  IconBuilding,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { publicTenderApi } from '../api/publicTenders';
import { Logo } from '../components/Logo';

export function PublicTenderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  const { data: tender, isLoading, error } = useQuery({
    queryKey: ['public-tender', id],
    queryFn: () => publicTenderApi.getTenderById(id!),
    enabled: !!id,
  });

  const { data: torDocument } = useQuery({
    queryKey: ['public-tor-document', id],
    queryFn: () => publicTenderApi.getTorDocument(id!),
    enabled: !!id,
  });

  const handleDownloadTor = async () => {
    if (!id) return;
    
    setDownloading(true);
    try {
      await publicTenderApi.downloadTorDocument(id);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Center>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error || !tender) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Error">
          Failed to load tender details. Please try again.
        </Alert>
      </Container>
    );
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'GOODS': return 'blue';
      case 'SERVICES': return 'green';
      case 'WORKS': return 'orange';
      default: return 'gray';
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'PUBLISHED': return 'green';
      case 'COLLECTION': return 'blue';
      case 'OPENED': return 'yellow';
      case 'TECH_EVAL': return 'purple';
      case 'FIN_EVAL': return 'indigo';
      case 'AWARDED': return 'teal';
      default: return 'gray';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 via-green-300 to-emerald-400">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <Container size="lg" className="py-4">
          <Group justify="space-between" align="center">
            <Group gap="md">
              <Logo height={48} />
              <Title order={1} size="h3" c="green.6">
                HMH Vendor Portal
              </Title>
            </Group>
            <Group gap="sm">
              <Button
                variant="subtle"
                onClick={() => navigate("/vendor/login")}
                color="green"
              >
                Sign In
              </Button>
              <Button onClick={() => navigate("/vendor/register")} color="green">
                Register Now
              </Button>
            </Group>
          </Group>
        </Container>
      </header>

      <Container size="lg" py="xl">
        <Stack gap="xl">
          {/* Page Header */}
          <Group justify="space-between" align="flex-start">
          <div>
            <Button
              component={Link}
              to="/public/tenders"
              leftSection={<IconArrowLeft size={16} />}
              variant="subtle"
              mb="md"
            >
              Back to Tenders
            </Button>
            <Title order={1} size="h2">
              {tender.description}
            </Title>
            <Text c="dimmed" size="lg" mt="xs">
              Reference: {tender.referenceNo}
            </Text>
          </div>
          <Group>
            <Badge color={getCategoryColor(tender.category)} size="lg">
              {tender.category}
            </Badge>
            <Badge color={getStageColor(tender.stage)} size="lg">
              {tender.stage.replace('_', ' ')}
            </Badge>
          </Group>
        </Group>

        <Grid>
          {/* Main Content */}
          <Grid.Col span={8}>
            <Stack gap="lg">
              {/* Tender Details */}
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Title order={3} size="h3" mb="lg">
                  Tender Details
                </Title>
                <Grid>
                  <Grid.Col span={6}>
                    <Stack gap="md">
                      <Group>
                        <IconCalendar size={20} />
                        <div>
                          <Text size="sm" c="dimmed">Publishing Date</Text>
                          <Text fw={500}>
                            {tender.publishingDate 
                              ? dayjs(tender.publishingDate).format('MMM DD, YYYY HH:mm')
                              : 'Not specified'
                            }
                          </Text>
                        </div>
                      </Group>
                      <Group>
                        <IconCalendar size={20} />
                        <div>
                          <Text size="sm" c="dimmed">Submission Deadline</Text>
                          <Text fw={500}>
                            {tender.submissionDeadline 
                              ? dayjs(tender.submissionDeadline).format('MMM DD, YYYY HH:mm')
                              : 'Not specified'
                            }
                          </Text>
                        </div>
                      </Group>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap="md">
                      {tender.openingDate && (
                        <Group>
                          <IconCalendar size={20} />
                          <div>
                            <Text size="sm" c="dimmed">Opening Date</Text>
                            <Text fw={500}>
                              {dayjs(tender.openingDate).format('MMM DD, YYYY HH:mm')}
                            </Text>
                          </div>
                        </Group>
                      )}
                      <Group>
                        <IconBuilding size={20} />
                        <div>
                          <Text size="sm" c="dimmed">Category</Text>
                          <Text fw={500}>{tender.category}</Text>
                        </div>
                      </Group>
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* TOR Specification */}
              {tender.torSpecification && (
                <Card shadow="sm" padding="xl" radius="md" withBorder>
                  <Title order={3} size="h3" mb="lg">
                    Terms of Reference (TOR) Specification
                  </Title>
                  <Paper p="md" withBorder>
                    <div 
                      dangerouslySetInnerHTML={{ __html: tender.torSpecification }}
                      style={{ lineHeight: 1.6 }}
                    />
                  </Paper>
                </Card>
              )}
            </Stack>
          </Grid.Col>

          {/* Sidebar */}
          <Grid.Col span={4}>
            <Stack gap="lg">
              {/* TOR Document */}
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Title order={3} size="h3" mb="lg">
                  TOR Document
                </Title>
                {torDocument ? (
                  <Stack gap="md">
                    <Group>
                      <IconFileText size={20} />
                      <div>
                        <Text fw={500} size="sm">{torDocument.fileName}</Text>
                        <Text size="xs" c="dimmed">
                          {(torDocument.fileSize / 1024 / 1024).toFixed(2)} MB
                        </Text>
                      </div>
                    </Group>
                    <Button
                      leftSection={<IconDownload size={16} />}
                      onClick={handleDownloadTor}
                      loading={downloading}
                      fullWidth
                    >
                      Download TOR Document
                    </Button>
                  </Stack>
                ) : (
                  <Alert color="yellow" title="No TOR Document">
                    No TOR document has been uploaded for this tender yet.
                  </Alert>
                )}
              </Card>

              {/* Important Dates */}
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Title order={3} size="h3" mb="lg">
                  Important Dates
                </Title>
                <Stack gap="md">
                  <div>
                    <Text size="sm" c="dimmed">Publishing Date</Text>
                    <Text fw={500} size="sm">
                      {tender.publishingDate 
                        ? dayjs(tender.publishingDate).format('MMM DD, YYYY')
                        : 'Not specified'
                      }
                    </Text>
                  </div>
                  <Divider />
                  <div>
                    <Text size="sm" c="dimmed">Submission Deadline</Text>
                    <Text fw={500} size="sm">
                      {tender.submissionDeadline 
                        ? dayjs(tender.submissionDeadline).format('MMM DD, YYYY')
                        : 'Not specified'
                      }
                    </Text>
                  </div>
                  {tender.openingDate && (
                    <>
                      <Divider />
                      <div>
                        <Text size="sm" c="dimmed">Opening Date</Text>
                        <Text fw={500} size="sm">
                          {dayjs(tender.openingDate).format('MMM DD, YYYY')}
                        </Text>
                      </div>
                    </>
                  )}
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
      </Container>
    </div>
  );
}
